import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Calendar as CalendarIcon, 
  Check 
} from 'lucide-react';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  isToday, 
  isFuture,
  parseISO
} from 'date-fns';

const ModernCalendarModal = ({ isOpen, onClose, onSelectDate, selectedDateStr, accentColor = '#111827' }) => {
  const initialDate = selectedDateStr ? parseISO(selectedDateStr) : new Date();
  const [currentMonth, setCurrentMonth] = useState(initialDate);
  const [selectedDay, setSelectedDay] = useState(selectedDateStr ? parseISO(selectedDateStr) : new Date());

  if (!isOpen) return null;

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleSelectDay = (day) => {
    if (isFuture(day) && !isToday(day)) return;
    setSelectedDay(day);
  };

  const handleApply = () => {
    if (selectedDay) {
      onSelectDate(format(selectedDay, 'yyyy-MM-dd'));
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/40 backdrop-blur-xs"
        />

        {/* Modal Sheet */}
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.96 }}
          transition={{ type: 'spring', damping: 26, stiffness: 320 }}
          className="relative w-full max-w-sm bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl z-10 space-y-4 border border-gray-100"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-800">
                <CalendarIcon size={16} />
              </div>
              <div>
                <h3 className="text-sm font-black text-gray-900">Select Date</h3>
                <p className="text-[11px] text-gray-400 font-semibold">Filter orders by calendar date</p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
            >
              <X size={14} />
            </button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between px-1 py-1">
            <h4 className="text-sm font-black text-gray-900">
              {format(currentMonth, 'MMMM yyyy')}
            </h4>

            <div className="flex items-center gap-1">
              <button
                onClick={handlePrevMonth}
                className="w-8 h-8 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200/60 flex items-center justify-center text-gray-700 transition-colors active:scale-95"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={handleNextMonth}
                disabled={isSameMonth(currentMonth, new Date())}
                className={`w-8 h-8 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200/60 flex items-center justify-center text-gray-700 transition-colors active:scale-95 ${
                  isSameMonth(currentMonth, new Date()) ? 'opacity-40 pointer-events-none' : ''
                }`}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Weekday Labels */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {weekDays.map((wd) => (
              <span key={wd} className="text-[10px] font-black text-gray-400 uppercase tracking-wider py-1">
                {wd}
              </span>
            ))}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const isSelected = selectedDay && isSameDay(day, selectedDay);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isTodayDate = isToday(day);
              const isFutureDate = isFuture(day) && !isTodayDate;

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => handleSelectDay(day)}
                  disabled={!isCurrentMonth || isFutureDate}
                  className={`h-9 rounded-xl text-xs font-bold transition-all relative flex flex-col items-center justify-center cursor-pointer ${
                    !isCurrentMonth
                      ? 'text-gray-200 pointer-events-none'
                      : isFutureDate
                      ? 'text-gray-300 pointer-events-none cursor-not-allowed'
                      : isSelected
                      ? 'bg-gray-900 text-white shadow-md font-black scale-105 z-10'
                      : 'text-gray-700 hover:bg-gray-100 active:scale-95'
                  }`}
                >
                  <span>{format(day, 'd')}</span>
                  {isTodayDate && !isSelected && (
                    <span className="w-1 h-1 rounded-full bg-red-500 absolute bottom-1.5" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Selected Date Summary & Actions */}
          <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-3">
            <div className="text-left">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Selected</span>
              <span className="text-xs font-black text-gray-900">
                {selectedDay ? format(selectedDay, 'dd MMM yyyy') : 'None'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-3.5 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 rounded-xl text-xs font-black bg-gray-900 text-white shadow-sm hover:bg-black transition-colors flex items-center gap-1.5 active:scale-95 cursor-pointer"
              >
                <Check size={14} />
                <span>Apply Date</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ModernCalendarModal;
